import React from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Crest } from "@/components/Crest";
import { SimoAI } from "@/components/SimoAI";
import { LayoutDashboard, KanbanSquare, Sparkles, ShieldCheck, LogOut, Image, Users, Share2, Contact, Upload, FileText, FolderOpen, ShoppingBag, Send, Workflow, Inbox, Rocket, GraduationCap, Wand2, Palette, BarChart3, PlugZap, Megaphone, Menu } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/autopilot", label: "Autopilot", icon: Rocket },
  { to: "/admin/crm", label: "Admissions CRM", icon: KanbanSquare },
  { to: "/admin/contacts", label: "Contacts", icon: Contact },
  { to: "/admin/sitf", label: "Trajectory (SITF)", icon: GraduationCap },
  { to: "/admin/sitf/cohort", label: "Cohort Dashboard", icon: BarChart3 },
  { to: "/admin/campaigns", label: "Campaigns", icon: Send },
  { to: "/admin/growth", label: "Growth Engine", icon: Megaphone },
  { to: "/admin/automations", label: "Automations", icon: Workflow },
  { to: "/admin/inbox", label: "Simo Inbox", icon: Inbox },
  { to: "/admin/integrations", label: "Integrations", icon: PlugZap },
  { to: "/admin/imports", label: "Kajabi Import", icon: Upload },
  { to: "/admin/pages", label: "Pages", icon: FileText },
  { to: "/admin/doc-studio", label: "Doc Studio", icon: Wand2 },
  { to: "/admin/media", label: "Media Library", icon: FolderOpen },
  { to: "/admin/sales", label: "Sales", icon: ShoppingBag },
  { to: "/admin/tuition", label: "Tuition & Aid", icon: GraduationCap },
  { to: "/admin/faculty", label: "Faculty & Staff", icon: Users },
  { to: "/admin/connect", label: "Conversion Hub", icon: Share2 },
  { to: "/admin/ai", label: "Ask Simo AI", icon: Sparkles },
  { to: "/admin/appearance", label: "Appearance", icon: Image },
  { to: "/admin/branding", label: "Branding", icon: Palette },
  { to: "/admin/registry", label: "Registry", icon: ShieldCheck },
];

export function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-ink/40">Loading…</div>;
  if (!user) return <Navigate to="/staff/login" replace />;
  if (!ADMIN_OK(user.role)) return <Navigate to={["parent", "student"].includes(user.role) ? "/portal" : "/centre"} replace />;
  return children;
}

const ADMIN_OK = (r) => !["applicant", "parent", "student"].includes(r);

export function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => { setOpen(false); }, [loc.pathname]);
  return (
    <div className="h-screen flex bg-[#0B0D12] text-paper overflow-hidden">
      {/* Mobile backdrop */}
      {open && <div className="fixed inset-0 z-40 bg-black/60 md:hidden" onClick={() => setOpen(false)} data-testid="admin-nav-backdrop" />}
      <aside className={`w-64 max-w-[82vw] border-r border-white/10 flex flex-col shrink-0 h-screen bg-[#0B0D12] z-50
                         fixed inset-y-0 left-0 transform transition-transform duration-200 md:static md:translate-x-0
                         ${open ? "translate-x-0" : "-translate-x-full"}`} data-testid="admin-sidebar">
        <Link to="/" className="flex items-center gap-2.5 px-5 h-16 border-b border-white/10 shrink-0">
          <Crest className="h-8 w-8" /><span className="font-serif text-lg">Simo · Admin</span>
        </Link>
        <nav className="flex-1 min-h-0 overflow-y-auto p-3 space-y-1">
          {NAV.map((n) => {
            const active = n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
            return (
              <Link key={n.to} to={n.to} data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${active ? "bg-heraldic text-white" : "text-paper/60 hover:bg-white/5 hover:text-paper"}`}>
                <n.icon className="h-4 w-4" /> {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10 shrink-0">
          <div className="px-3 py-2 text-xs text-paper/50">{user?.name}<div className="text-heraldic capitalize">{user?.role}</div></div>
          <button data-testid="admin-logout" onClick={() => { logout(); nav("/staff/login"); }} className="flex items-center gap-2 px-3 py-2 text-sm text-paper/60 hover:text-paper w-full"><LogOut className="h-4 w-4" /> Log out</button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0 h-screen">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center gap-3 h-14 px-4 border-b border-white/10 shrink-0">
          <button onClick={() => setOpen(true)} data-testid="admin-nav-toggle" className="p-1.5 -ml-1.5 text-paper/80"><Menu className="h-6 w-6" /></button>
          <Crest className="h-6 w-6" /><span className="font-serif text-base">Simo · Admin</span>
        </div>
        <main className="flex-1 overflow-auto min-w-0">{children}</main>
      </div>
      <SimoAI />
    </div>
  );
}
